/**
 * The list URL is the list state.
 *
 * A filtered queue is something agents send each other ("look at the
 * unassigned refunds"), so the filter set round-trips through the route query
 * rather than living only in the store: `filtersFromQuery` is what the screen
 * boots from, `queryFromFilters` is what it writes back, and the two are
 * inverses for every reachable filter set.
 *
 * Values are passed through verbatim rather than validated against the enums.
 * The API contract is explicit that a filter the server does not understand
 * must not read as no filter at all (`docs/ticket-engine/api.md`): a category
 * this bundle has never heard of matches no stage and returns no rows, which is
 * the honest answer. Sanitising it to `null` would silently widen the query.
 *
 * Only non-default values are written, so a shared URL carries exactly what it
 * narrows and `/cases` alone means "everything".
 */
import { OWNER_SCOPE, SORT_OPTION } from 'dashboard/helper/supportCaseMeta';
import { DEFAULT_FILTERS } from 'dashboard/stores/supportCases';

// Ids arrive from the URL as strings; the store and the REST contract want
// numbers. Anything that is not a positive integer is no filter.
const toId = value => {
  const id = Number(value);
  return Number.isInteger(id) && id > 0 ? id : null;
};

const toPage = value => {
  const page = Number(value);
  return Number.isInteger(page) && page > 0 ? page : 1;
};

/**
 * @param {Object} query the route's query object
 * @returns {Object} a complete filter set — dropping a query param resets that
 *   filter to its default rather than leaving the previous value behind
 */
export const filtersFromQuery = (query = {}) => ({
  ...DEFAULT_FILTERS,
  caseTypeId: toId(query.type),
  stageId: toId(query.stage),
  category: query.category || null,
  owner: query.owner || OWNER_SCOPE.ALL,
  archived: query.archived === 'true',
  q: query.q || '',
  sortBy: query.sort || SORT_OPTION.LAST_ACTIVITY,
  page: toPage(query.page),
});

/**
 * @param {Object} filters the store's filter set
 * @returns {Object} the route query, defaults omitted
 */
export const queryFromFilters = (filters = {}) => ({
  ...(filters.caseTypeId ? { type: String(filters.caseTypeId) } : {}),
  ...(filters.stageId ? { stage: String(filters.stageId) } : {}),
  ...(filters.category ? { category: filters.category } : {}),
  ...(filters.owner && filters.owner !== OWNER_SCOPE.ALL
    ? { owner: filters.owner }
    : {}),
  ...(filters.archived ? { archived: 'true' } : {}),
  ...(filters.q ? { q: filters.q } : {}),
  ...(filters.sortBy && filters.sortBy !== SORT_OPTION.LAST_ACTIVITY
    ? { sort: filters.sortBy }
    : {}),
  ...(filters.page > 1 ? { page: String(filters.page) } : {}),
});

// Whether the queue is narrowed at all — the difference between "no tickets
// yet" and "no tickets match these filters", which are different screens.
// Sort and page order a result set, they do not narrow one, so neither counts.
export const hasActiveFilters = (filters = {}) =>
  Object.keys(
    queryFromFilters({
      ...filters,
      page: 1,
      sortBy: SORT_OPTION.LAST_ACTIVITY,
    })
  ).length > 0;
